import type { DragEvent, KeyboardEvent, ReactNode } from "react";
import { Image as ImageIcon, Loader2, Plus } from "lucide-react";

/**
 * Abanico de tres "fotos" con un + azul: el dibujo de "subir fotos de un producto" de
 * Mi marca (ADR-2026-10-05-mi-marca-tarjeta-de-subir-producto). Lo usan la primera
 * tarjeta de la grilla y la zona de subida del formulario de alta
 * (ADR-2026-10-06-mi-marca-alta-de-producto-con-su-cara), para que sean el mismo objeto.
 *
 * Se abre con el hover de un ancestro con la clase `group`. Colores en hex y no
 * `brand-primary/10`: los tokens `brand-*` no aceptan opacidad y esa clase no genera CSS.
 *
 * `frente` reemplaza el ícono de la foto del frente por un dibujo que la ocupa entera: las
 * tarjetas de alta de "Elegí qué agregar" ponen ahí un retrato o una figura en pose
 * (`dibujosDeAlta`), para que se reconozca qué se agrega.
 */
export const AbanicoDeFotos = ({
  cargando = false,
  frente,
}: {
  cargando?: boolean;
  frente?: ReactNode;
}) => (
  <span aria-hidden className="relative block h-[60px] w-[88px] sm:h-[72px] sm:w-[104px]">
    <span className="absolute left-1/2 top-1/2 h-[50px] w-10 -translate-x-[95%] -translate-y-1/2 -rotate-12 rounded-lg bg-[rgb(var(--brand-primary-rgb)_/_0.14)] shadow-[0_1px_2px_rgba(16,24,40,0.06)] transition-transform duration-300 ease-out group-hover:-translate-x-[115%] group-hover:-rotate-[18deg] sm:h-[60px] sm:w-12" />
    <span className="absolute left-1/2 top-1/2 h-[50px] w-10 -translate-x-[5%] -translate-y-1/2 rotate-12 rounded-lg bg-[rgb(var(--brand-primary-rgb)_/_0.09)] shadow-[0_1px_2px_rgba(16,24,40,0.06)] transition-transform duration-300 ease-out group-hover:translate-x-[15%] group-hover:rotate-[18deg] sm:h-[60px] sm:w-12" />
    <span className="absolute left-1/2 top-1/2 flex h-[54px] w-[42px] -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-lg bg-white shadow-[0_6px_16px_-6px_rgba(16,24,40,0.25),0_0_0_1px_rgba(16,24,40,0.06)] transition-transform duration-300 ease-out group-hover:-translate-y-[58%] sm:h-16 sm:w-[50px]">
      {/* Recortado aparte (y no con `overflow-hidden` en la foto) para no cortar el "+". */}
      {frente && !cargando && (
        <span className="absolute inset-0 overflow-hidden rounded-lg">{frente}</span>
      )}
      {cargando ? (
        <Loader2 className="h-[18px] w-[18px] animate-spin text-brand-primary sm:h-5 sm:w-5" />
      ) : frente ? null : (
        <ImageIcon className="h-[18px] w-[18px] text-[rgb(var(--brand-primary-rgb)_/_0.5)] sm:h-5 sm:w-5" strokeWidth={1.75} />
      )}
      <span className="absolute -bottom-2 -right-2 flex h-6 w-6 items-center justify-center rounded-full bg-brand-primary text-white shadow-[0_4px_10px_-2px_rgb(var(--brand-primary-rgb)_/_0.55)] ring-[2.5px] ring-white transition-transform duration-300 group-hover:scale-110 sm:h-7 sm:w-7">
        <Plus className="h-3.5 w-3.5 sm:h-4 sm:w-4" strokeWidth={2.75} />
      </span>
    </span>
  </span>
);

/**
 * Lo que le falta a un `<label>` de un input de archivos oculto para usarse con teclado: el
 * input `hidden` no recibe foco y el label tampoco, así que sin esto Tab lo saltea
 * (ADR-2026-10-07-las-zonas-de-subida-se-usan-con-teclado). Enter o Espacio abren el
 * selector, como el click.
 */
export const abrirConTeclado = (htmlFor: string, deshabilitado = false) => ({
  role: "button" as const,
  tabIndex: deshabilitado ? -1 : 0,
  "aria-disabled": deshabilitado || undefined,
  onKeyDown: (e: KeyboardEvent) => {
    if (deshabilitado || (e.key !== "Enter" && e.key !== " ")) return;
    e.preventDefault();
    (document.getElementById(htmlFor) as HTMLInputElement | null)?.click();
  },
});

/**
 * "Agregá otros ángulos del producto": la tarjeta punteada, como última de una grilla de
 * fotos. Es un `<label>` del input de archivos que se le pasa; la usan el alta y la edición
 * de un producto, para que las dos grillas sean la misma.
 */
export const TarjetaDeAgregarFoto = ({
  htmlFor,
  cargando = false,
  arrastrando = false,
  textoCargando = "Preparando…",
  ...soltar
}: {
  htmlFor: string;
  cargando?: boolean;
  arrastrando?: boolean;
  textoCargando?: string;
  onDragOver?: (e: DragEvent) => void;
  onDragLeave?: () => void;
  onDrop?: (e: DragEvent) => void;
}) => (
  <label
    htmlFor={htmlFor}
    {...soltar}
    {...abrirConTeclado(htmlFor, cargando)}
    className={`group relative flex aspect-square cursor-pointer flex-col items-center justify-center gap-3 overflow-hidden rounded-2xl border-[1.5px] border-dashed text-center outline-none transition-[border-color,box-shadow] duration-300 focus-visible:ring-2 focus-visible:ring-brand-primary focus-visible:ring-offset-2 ${
      cargando ? "pointer-events-none" : ""
    } ${
      arrastrando
        ? "border-brand-primary bg-[rgb(var(--brand-primary-rgb)_/_0.085)]"
        : "border-[rgb(var(--brand-primary-rgb)_/_0.215)] bg-gradient-to-b from-white to-[rgb(var(--brand-primary-rgb)_/_0.055)] hover:border-brand-primary hover:shadow-[0_12px_32px_-14px_rgb(var(--brand-primary-rgb)_/_0.45)]"
    }`}
  >
    {/* Con una foto ya cargada, lo que falta son OTRAS VISTAS del mismo producto: la
        ilustración son tres ángulos de una remera, no el abanico genérico. */}
    {cargando ? (
      <AbanicoDeFotos cargando />
    ) : (
      <img
        src="/images/subir-productos/otros-angulos-azul.png"
        alt=""
        aria-hidden
        draggable={false}
        className="pointer-events-none w-[72%] max-w-[260px] select-none transition-transform duration-300 ease-out group-hover:scale-[1.04]"
      />
    )}
    <span className="px-3 text-[13px] font-semibold text-[#16181D]">
      {cargando ? textoCargando : "Agregá otros ángulos del producto"}
    </span>
  </label>
);
